/**
 * Card readers (Stripe Terminal) — the desk-side half of a reader payment.
 *
 * Starting a charge lives on the surface that owns the money (createPosSale
 * with payment_method "card_reader", acceptPayment / processPayment with a
 * card_reader method). This module is everything else: the readers to pick
 * from, polling a charge until the tap lands, canceling one, and the setup
 * calls the POS settings page makes.
 *
 * Failure shape mirrors the success shape (flat `{ success, error }`), the
 * same rule posAPI follows — see paymentsAPI.js for why that matters.
 */
import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

const headers = (token: string) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

const failure = (err: any, fallback: string) => ({
  success: false as const,
  error: err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback,
  code: err?.response?.data?.code as string | undefined,
  // The HTTP status. Named so it can never collide with a charge's own
  // `status` (waiting | succeeded | failed | canceled) on ReaderChargeResponse.
  httpStatus: err?.response?.status as number | undefined,
});

export interface TerminalReader {
  id: string;
  label: string | null;
  status: string | null;
  device_type: string | null;
  serial_number: string | null;
  location: string | null;
  simulated: boolean;
  action: { type: string; status: string; payment_intent: string | null } | null;
}

export interface TerminalLocation {
  id: string;
  display_name: string;
  address: {
    line1?: string;
    line2?: string | null;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  } | null;
}

export interface ReadersResponse {
  success: boolean;
  terminal_enabled?: boolean;
  location?: TerminalLocation | null;
  readers?: TerminalReader[];
  error?: string;
}

export type ReaderChargeStatus = "waiting" | "succeeded" | "failed" | "canceled";

export interface ReaderChargeResponse {
  success: boolean;
  status?: ReaderChargeStatus;
  surface?: "pos" | "member_payment" | "invoice_payment";
  message?: string | null;
  decline_code?: string | null;
  recorded?: boolean;
  alreadyRecorded?: boolean;
  payment?: any;
  order?: any;
  invoice?: any;
  card?: { saved: boolean; paymentMethodId: string | null; madeDefault: boolean; reason: string | null } | null;
  receiptDropped?: boolean;
  /** Set on the "charged but not saved" 500 — staff must NOT retry. */
  charged?: boolean;
  paymentIntentId?: string;
  error?: string;
}

export const listReaders = async (token: string): Promise<ReadersResponse> => {
  try {
    const res = await axios.get(`${BASE_URL}/terminal/readers`, { headers: headers(token) });
    return res.data;
  } catch (err: any) {
    return failure(err, "Failed to load card readers");
  }
};

export const getReaderCharge = async (
  token: string,
  paymentIntentId: string
): Promise<ReaderChargeResponse> => {
  try {
    const res = await axios.get(`${BASE_URL}/terminal/charges/${encodeURIComponent(paymentIntentId)}`, {
      headers: headers(token),
    });
    return res.data;
  } catch (err: any) {
    // The recorder's "charged but not saved" answer arrives as a 500 with
    // `charged: true` — keep that flag; it is the one case staff must not retry.
    return { ...failure(err, "Failed to check the card reader payment"), charged: !!err?.response?.data?.charged, paymentIntentId: err?.response?.data?.paymentIntentId };
  }
};

export const cancelReaderCharge = async (
  token: string,
  paymentIntentId: string,
  readerId?: string | null
): Promise<ReaderChargeResponse> => {
  try {
    const res = await axios.post(
      `${BASE_URL}/terminal/charges/${encodeURIComponent(paymentIntentId)}/cancel`,
      { reader_id: readerId || null },
      { headers: headers(token) }
    );
    return res.data;
  } catch (err: any) {
    return failure(err, "Failed to cancel the card reader payment");
  }
};

export const saveReaderLocation = async (
  token: string,
  payload: { display_name: string; address: NonNullable<TerminalLocation["address"]> }
): Promise<{ success: boolean; location?: TerminalLocation; error?: string }> => {
  try {
    const res = await axios.post(`${BASE_URL}/terminal/location`, payload, { headers: headers(token) });
    return res.data;
  } catch (err: any) {
    return failure(err, "Failed to save the reader location");
  }
};

export const registerReader = async (
  token: string,
  payload: { registration_code: string; label: string }
): Promise<{ success: boolean; reader?: TerminalReader; error?: string }> => {
  try {
    const res = await axios.post(`${BASE_URL}/terminal/readers`, payload, { headers: headers(token) });
    return res.data;
  } catch (err: any) {
    return failure(err, "Failed to register the reader");
  }
};

export const removeReader = async (
  token: string,
  readerId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await axios.delete(`${BASE_URL}/terminal/readers/${encodeURIComponent(readerId)}`, {
      headers: headers(token),
    });
    return res.data;
  } catch (err: any) {
    return failure(err, "Failed to remove the reader");
  }
};

/** Test mode only: pretend a card was tapped on a simulated reader. */
export const simulateReaderTap = async (
  token: string,
  readerId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await axios.post(
      `${BASE_URL}/terminal/readers/${encodeURIComponent(readerId)}/simulate-tap`,
      {},
      { headers: headers(token) }
    );
    return res.data;
  } catch (err: any) {
    return failure(err, "Failed to simulate a tap");
  }
};
